import { useReducer } from "react";
import { useMutation } from "@tanstack/react-query";

import { track } from "@/lib/analytics/data-layer";
import { completeOnboardingStep, finishOnboarding } from "@/lib/analytics/onboarding-funnel";
import { clientPost } from "@/lib/http/client-http";
import { leaveForDocument } from "@/lib/motion/page-transition";
import { CODE_ERRORS, LOGIN_FALLBACK_ERROR, LOGIN_TRANSPORT_ERROR, RESEND_CODE_ERROR } from "@/lib/errors/error-messages";
import type { EightDigitCodeValues } from "@/lib/auth/code-schemas";
import { listCreationDestination, type OnboardingIntent } from "@/lib/onboarding/onboarding-intent";

// ─── Types ───────────────────────────────────────────────────────────────────

type LoginStep = "otp";

type LoginState = {
  step: LoginStep;
  error: string | null;
  /**
   * The code was accepted and the next page is loading. The cells stay locked until it arrives:
   * only a refused code hands them back.
   */
  leaving: boolean;
};

type LoginAction =
  | { type: "SET_ERROR"; error: string | null }
  | { type: "CLEAR" }
  | { type: "LEAVE" };

/**
 * The route answers with the list it provisioned or found, and whether its terms are still
 * pending (`/api/auth/customer/login/confirm`). This hook used to declare only `{ ok: true }`
 * and throw the rest away — see `onSuccess`.
 */
type ConfirmLoginResponse =
  | { ok: true; giftList?: { needsTermsAcceptance?: boolean } | null }
  | { ok: false; code: "INVALID_CODE" | "EXPIRED" | "SERVER_ERROR" };
type ResendResponse = { ok: true } | { ok: false };

// ─── Reducer ─────────────────────────────────────────────────────────────────

function reducer(state: LoginState, action: LoginAction): LoginState {
  switch (action.type) {
    case "SET_ERROR":
      // An error is the one thing that hands the cells back.
      return { ...state, error: action.error, leaving: action.error === null ? state.leaving : false };
    case "CLEAR":
      return { step: "otp", error: null, leaving: false };
    case "LEAVE":
      return { ...state, error: null, leaving: true };
    default:
      return state;
  }
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export interface UseLoginWizardResult {
  step: LoginStep;
  error: string | null;
  isPending: boolean;
  isResending: boolean;
  actions: {
    confirmCode: (values: EightDigitCodeValues) => void;
    resend: () => void;
  };
}

export function useLoginWizard(
  redirectTo = "/anfitriao/dashboard",
  intent: OnboardingIntent = "host",
): UseLoginWizardResult {
  const [state, dispatch] = useReducer(reducer, { step: "otp", error: null, leaving: false });

  const resendMutation = useMutation({
    mutationFn: () => clientPost<ResendResponse>("/api/auth/customer/login/resend"),
    onSuccess: (data) => {
      if (!data.ok) {
        dispatch({ type: "SET_ERROR", error: RESEND_CODE_ERROR });
      }
    },
    onError: () => dispatch({ type: "SET_ERROR", error: RESEND_CODE_ERROR }),
  });

  const confirmMutation = useMutation({
    mutationFn: (values: EightDigitCodeValues) =>
      clientPost<ConfirmLoginResponse>("/api/auth/customer/login/confirm", { ...values, intent }),
    onSuccess: (data) => {
      if (!data.ok) {
        dispatch({ type: "SET_ERROR", error: CODE_ERRORS[data.code] ?? LOGIN_FALLBACK_ERROR });
        return;
      }
      dispatch({ type: "LEAVE" });

      // An existing account came in by CPF and code: the funnel ends here, in the panel or in the
      // creation the terms send it to (that one is a funnel of its own, which starts again).
      completeOnboardingStep("login_code");
      track({ event: "login", method: "cpf_otp" });
      finishOnboarding();

      /*
       * A host whose list still needs the terms goes where the terms are asked — the creation,
       * which accepts them and then sends them on by where the list stands
       * (`afterCreateListTermsHref`).
       *
       * This hook used to discard the `giftList` the route returns, so that person reached the
       * dashboard, was bounced by the panel's guard, and only then met the terms. It worked by
       * accident, and the accident is what made the consultant's client redo a finished list
       * (reported 2026-10-08). A guest has no list and no terms to accept, so only the host
       * door asks.
       */
      const needsTerms = intent === "host" && data.giftList?.needsTermsAcceptance === true;

      // Full page navigation (not client-side) — ensures the session cookie
      // from the POST response is available before the dashboard loads.
      leaveForDocument(needsTerms ? listCreationDestination(redirectTo) : redirectTo);
    },
    onError: () => dispatch({ type: "SET_ERROR", error: LOGIN_TRANSPORT_ERROR }),
  });

  return {
    step: state.step,
    error: state.error,
    isPending: confirmMutation.isPending || state.leaving,
    isResending: resendMutation.isPending,
    actions: {
      confirmCode: (values) => {
        dispatch({ type: "SET_ERROR", error: null });
        confirmMutation.mutate(values);
      },
      resend: () => resendMutation.mutate(),
    },
  };
}
